import React, { useEffect, useMemo, useState } from 'react';
import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import Stack from '@mui/material/Stack';
import GlobalSelectFieldWithoutFormikNew from '../../../components/mui/GlobalSelectFieldWithoutFormikNew';
import { expiringAdmissionHeader } from './utils/expiringAdmissionHeader';
import { useDispatch } from 'react-redux';
import { isSameUserType } from '../../../utils/helperFunction';
import { getCROUnderProject } from '../../../redux/features/CRDslice';
import { getAllProjects } from '../../../redux/features/courseSlice';
import { getExpiringAdmissionTable } from '../../../redux/features/crdMigrationSlice';
import { croTypeNames } from '../../../utils/employeeTypes';
import { useSelector } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import GlobalTableNoPagination from '../../../components/CustomTableWithPagination/GlobalTableNoPagination';
import ChangeExpiringAdmissionStatus from './components/ChangeExpiringAdmissionStatus';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import GlobalAutoComplete from '../../../components/GlobalAutoComplete';

export default function ExpiringAdmission() {
  const dispatch = useDispatch();

  // Redux State-------------------------------------------------------------
  const allProjects = useSelector((state) => state.course.projects);
  const crosunderproject = useSelector((state) => state.crd.allCroUnderProject);
  const crosunderprojectLoading = useSelector((state) => state.crd.loading);
  const courseLoading = useSelector((state) => state.course.loading);

  const loading = useSelector((state) => state.crdMigration.expiringAdmissionTable.loading);
  const tableData = useSelector((state) => state.crdMigration.expiringAdmissionTable.data);
  //--------------------------------------------------------

  // react state
  const [project, setProject] = useState({ id: '', name: '' });
  const [cro, setCRO] = useState(null);
  const [yearAndMonth, setYearAndMonth] = useState(dayjs().format('YYYY-MM'));
  const [refresh, setRefresh] = useState(false);
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: 'asc' },
    frozenColumns: [],
    hiddenColumns: [],
  });
  // checking is CRO or not
  const isCro = useMemo(() => {
    const profile = JSON.parse(localStorage.getItem('cred')).profile;
    return isSameUserType({ roleArray: croTypeNames, role: profile?.role });
  }, []);

  // Fetch  Projects All roles ---------------------------------------------
  useEffect(() => {
    dispatch(getAllProjects());
  }, [dispatch]);

  //--------------------------------------------

  // -----------fetch cro list (pm,Crm only) ------
  useEffect(() => {
    if (!isCro) {
      dispatch(getCROUnderProject(project?.id ?? -1));
      setCRO({ id: '', name: '' });
    }
  }, [project, dispatch, isCro]);
  // ---------------------------------------------------------

  //---------- DispatchParams ---------------------------------
  const dispatchParams = useMemo(
    () => ({
      project_id: project?.id ?? null,
      cro_id: isCro ? null : (cro?.userID ?? null),
      month: Number(yearAndMonth?.split('-')?.[1]) - 1 ?? null,
      year: yearAndMonth?.split('-')?.[0] || null,
    }),
    [project, yearAndMonth, cro, isCro],
  );

  //---------* =-=-=-==-=--=-=-=-=- *---------------------------

  //-------------- Table Fetch ------------------------------
  useEffect(() => {
    const thunk = dispatch(getExpiringAdmissionTable(dispatchParams));

    return () => thunk?.abort();
  }, [dispatchParams, dispatch, refresh]);
  //-------------* Table Fetch *-----------------------------

  const [modalState, setModalState] = useState({
    actionKey: '',
    row: null,
  });

  const modalConfigs = useMemo(
    () => [
      {
        actionKey: 'Change Status',
        title: 'Change Status',
        maxWidth: 'md',
        render: ({ row }) => (
          <ChangeExpiringAdmissionStatus row={row} setOpenModalState={setModalState} setRefresh={setRefresh} />
        ),
      },
    ],
    [],
  );

  const activeConfig = useMemo(() => {
    if (!Array.isArray(modalConfigs) || !modalState.actionKey || !modalState.row) return null;
    return modalConfigs.find((c) => c?.actionKey === modalState.actionKey) || null;
  }, [modalConfigs, modalState.actionKey, modalState.row]);

  return (
    <GlobalWrapper title={'Expiring Admissions'}>
      <Stack gap={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={['month', 'year']}
              label="Year And Month"
              value={yearAndMonth ? dayjs(yearAndMonth) : null}
              onChange={(v) => {
                setYearAndMonth(v?.isValid() ? v.format('YYYY-MM') : null);
              }}
            />
          </LocalizationProvider>
          <GlobalSelectFieldWithoutFormikNew
            label={'Project'}
            data={[{ id: -1, name: 'All' }, ...allProjects]}
            keys={{
              itemId: 'id',
              itemValue: 'name',
            }}
            selectedDetails={project}
            setChange={setProject}
            sx={{
              width: { xs: '100%', sm: 200 },
            }}
            disabled={courseLoading}
          />
          {!isCro && (
            <GlobalAutoComplete
              data={crosunderproject}
              loading={crosunderprojectLoading}
              label="CRO"
              keyName={{
                id: 'userID',
                value: 'name',
              }}
              selectValue={cro}
              setSelectValue={setCRO}
              sx={{ width: { xs: '100%', sm: 200 } }}
            />
          )}

          {/* cro list for pm crd  */}
        </Stack>
        <GlobalTableNoPagination
          headers={expiringAdmissionHeader({ setModalState, isCro })}
          tableState={tableState}
          setTableState={setTableState}
          rowUniqueId="course_enrollment_id"
          loading={loading}
          rows={tableData}
        />
      </Stack>
      <GlobalDialog
        title={activeConfig?.title}
        open={Boolean(modalState?.actionKey)}
        setOpen={(val) => {
          if (!val) setModalState({ actionKey: '', row: null });
        }}
        closeButton={true}
        content={activeConfig?.render ? activeConfig.render({ row: modalState.row }) : null}
        maxWidth={activeConfig?.maxWidth || 'md'}
        fullWidth={true}
      />
    </GlobalWrapper>
  );
}
